import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, Pencil, Plus, Trash2, UsersRound, Workflow as WorkflowIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "../../i18n";
import { useBoundStore } from "../../store";
import { listStandardAgreements, StandardAgreement } from "../../services/StandardAgreements";
import {
  createWorkflow,
  deleteWorkflow,
  listWorkflows,
  updateWorkflow,
  Workflow,
  WorkflowInput,
  WorkflowKind,
} from "../../services/Workflows";

const NONE = "__none__";
const empty: WorkflowInput = {
  name: "",
  description: "",
  instructions: "",
  kind: "review",
  playbookId: null,
  standardAgreementId: null,
  visibility: "private",
};

/** Saved tasks, run from the chat with / (Playbooks page, Workflows tab). */
const WorkflowsTab: React.FC = () => {
  const { t } = useI18n();
  const w = t.playbooks.workflows;
  const playbooks = useBoundStore((state) => state.playbooks);
  const [items, setItems] = useState<Workflow[]>([]);
  const [standards, setStandards] = useState<StandardAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ id: string | null; data: WorkflowInput } | null>(null);
  const [deleting, setDeleting] = useState<Workflow | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await listWorkflows();
    if (res.success && res.data) setItems(res.data);
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    void listStandardAgreements().then((res) => res.success && res.data && setStandards(res.data));
  }, [load]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const res = editing.id ? await updateWorkflow(editing.id, editing.data) : await createWorkflow(editing.data);
      if (res.success) {
        toast.success(w.saved);
        setEditing(null);
        await load();
      } else {
        toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || w.failed);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      const res = await deleteWorkflow(deleting.id);
      if (res.success) {
        toast.success(w.deleted);
        setDeleting(null);
        await load();
      } else {
        toast.error(w.failed);
      }
    } finally {
      setBusy(false);
    }
  };

  const d = editing?.data;
  const setData = (patch: Partial<WorkflowInput>) => setEditing((e) => (e ? { ...e, data: { ...e.data, ...patch } } : e));
  const valid = !!d && d.name.trim() && d.instructions.trim();

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>{w.title}</CardTitle>
            <CardDescription>{w.description}</CardDescription>
          </div>
          <Button onClick={() => setEditing({ id: null, data: { ...empty } })}>
            <Plus className="mr-2 h-4 w-4" />
            {w.create}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <button
              type="button"
              onClick={() => setEditing({ id: null, data: { ...empty } })}
              className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center hover:bg-muted/40"
            >
              <WorkflowIcon className="h-8 w-8 text-muted-foreground" />
              <div className="font-medium">{w.empty}</div>
              <div className="max-w-md text-sm text-muted-foreground">{w.emptyHint}</div>
            </button>
          ) : (
            <div className="divide-y">
              {items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 py-3">
                  <WorkflowIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{item.name}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{w.kinds[item.kind]}</span>
                      {item.visibility === "organization" && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <UsersRound className="h-3 w-3" />
                          {w.shared}
                          {item.ownerName ? ` · ${w.by(item.ownerName)}` : ""}
                        </span>
                      )}
                    </div>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{item.description || item.instructions}</p>
                  </div>
                  {item.canEdit && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0" aria-label={w.edit}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            setEditing({
                              id: item.id,
                              data: {
                                name: item.name,
                                description: item.description ?? "",
                                instructions: item.instructions,
                                kind: item.kind,
                                playbookId: item.playbookId,
                                standardAgreementId: item.standardAgreementId,
                                visibility: item.visibility,
                              },
                            })
                          }
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          {w.edit}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => setDeleting(item)}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          {w.delete}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editing != null} onOpenChange={(open) => !open && !busy && setEditing(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? w.edit : w.create}</DialogTitle>
          </DialogHeader>
          {d && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="wf-name">{w.name}</Label>
                <Input id="wf-name" maxLength={120} placeholder={w.namePlaceholder} value={d.name} onChange={(e) => setData({ name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-kind">{w.kind}</Label>
                <Select value={d.kind} onValueChange={(v) => setData({ kind: v as WorkflowKind })}>
                  <SelectTrigger id="wf-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["review", "compare", "summarize", "prompt"] as WorkflowKind[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {w.kinds[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {d.kind === "review" && (
                <div className="space-y-1.5">
                  <Label htmlFor="wf-playbook">{w.playbook}</Label>
                  <Select value={d.playbookId ?? NONE} onValueChange={(v) => setData({ playbookId: v === NONE ? null : v })}>
                    <SelectTrigger id="wf-playbook">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{w.none}</SelectItem>
                      {playbooks.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {d.kind === "compare" && (
                <div className="space-y-1.5">
                  <Label htmlFor="wf-standard">{w.standardAgreement}</Label>
                  <Select
                    value={d.standardAgreementId ?? NONE}
                    onValueChange={(v) => setData({ standardAgreementId: v === NONE ? null : v })}
                  >
                    <SelectTrigger id="wf-standard">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{w.none}</SelectItem>
                      {standards.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="wf-instructions">{w.instructions}</Label>
                <Textarea
                  id="wf-instructions"
                  rows={5}
                  maxLength={8000}
                  placeholder={w.instructionsPlaceholder}
                  value={d.instructions}
                  onChange={(e) => setData({ instructions: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">{w.instructionsHint}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-description">{w.descriptionLabel}</Label>
                <Input id="wf-description" maxLength={300} value={d.description ?? ""} onChange={(e) => setData({ description: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wf-visibility">{w.visibility}</Label>
                <Select value={d.visibility} onValueChange={(v) => setData({ visibility: v as WorkflowInput["visibility"] })}>
                  <SelectTrigger id="wf-visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">{w.private}</SelectItem>
                    <SelectItem value="organization">{w.organization}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>
              {w.cancel}
            </Button>
            <Button onClick={() => void save()} disabled={!valid || busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {w.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting != null} onOpenChange={(open) => !open && !busy && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{w.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{deleting ? w.deleteDescription(deleting.name) : null}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{w.cancel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void remove();
              }}
            >
              {w.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WorkflowsTab;
